import { addExpense, updateExpense } from '../store.js';
import { escapeHtml } from './dom.js';
import { formatDateIT } from './format.js';
import { categoryOptionsHtml } from './planned-cost.js';
import { trapModalHistory } from './modal-history.js';

let modalEl = null;
let dismissHistory = null;

function destroyModal() {
  modalEl?.remove();
  modalEl = null;
}

function destroyModalFromBack() {
  dismissHistory = null;
  destroyModal();
}

function closeModal() {
  const dismiss = dismissHistory;
  dismissHistory = null;
  if (dismiss) dismiss();
  else destroyModal();
}

/**
 * @param {object} opts
 * @param {string} opts.instanceId
 * @param {string} opts.dayKey
 * @param {string} opts.label
 * @param {number | null} opts.amount
 * @param {string} opts.category
 * @param {string} [opts.costPreview]
 * @param {string} [opts.amountHint]
 * @param {string} [opts.expenseId] — se presente, modifica invece di creare
 * @param {Array<{ value: string, label: string }>} [opts.dayOptions] — select giorni viaggio
 * @param {() => void} [opts.onSuccess]
 */
export function openExpenseQuickAdd({
  instanceId,
  dayKey,
  label,
  amount,
  category,
  expenseId = null,
  costPreview = '',
  amountHint = '',
  dayOptions = null,
  onSuccess,
}) {
  closeModal();

  const isEdit = Boolean(expenseId);
  const amountValue = amount != null ? String(amount) : '';
  const hint = costPreview
    ? `<p class="expense-quick__preview">Previsione: ${escapeHtml(costPreview)}${amountHint ? ` · suggerito ${escapeHtml(amountHint)}` : ''}</p>`
    : '';
  const dialogLabel = isEdit ? 'Modifica spesa' : 'Registra spesa';
  const confirmLabel = isEdit ? 'Salva modifiche' : 'Conferma spesa';

  const dayField = (() => {
    if (isEdit || !dayOptions?.length) {
      const readonly = isEdit ? '' : 'readonly';
      return `
        <label class="field">
          <span class="field-label">Giorno</span>
          <input class="field-input" name="dayKey" type="date" required value="${escapeHtml(dayKey)}" ${readonly} />
          <span class="field-hint">${escapeHtml(formatDateIT(dayKey))}</span>
        </label>
      `;
    }
    const options = dayOptions
      .map(
        (opt) =>
          `<option value="${escapeHtml(opt.value)}"${opt.value === dayKey ? ' selected' : ''}>${escapeHtml(opt.label)}</option>`,
      )
      .join('');
    return `
      <label class="field">
        <span class="field-label">Giorno</span>
        <select class="field-input" name="dayKey" required>${options}</select>
      </label>
    `;
  })();

  modalEl = document.createElement('div');
  modalEl.className = 'expense-quick-modal';
  modalEl.innerHTML = `
    <div class="expense-quick-modal__backdrop" data-close-expense></div>
    <div class="expense-quick-modal__panel" role="dialog" aria-label="${dialogLabel}">
      <header class="expense-quick-modal__head">
        <h2 class="expense-quick-modal__title">${dialogLabel}</h2>
        <button type="button" class="btn-ghost btn-ghost--sm" data-close-expense>Chiudi</button>
      </header>
      ${hint}
      <form class="expense-quick-form" id="expense-quick-form">
        ${dayField}
        <label class="field">
          <span class="field-label">Descrizione</span>
          <input class="field-input" name="label" required value="${escapeHtml(label)}" />
        </label>
        <div class="custom-form__row">
          <label class="field">
            <span class="field-label">Importo (€)</span>
            <input class="field-input" name="amount" type="number" min="0" step="0.01" required placeholder="0,00" value="${escapeHtml(amountValue)}" />
          </label>
          <label class="field">
            <span class="field-label">Categoria</span>
            <select class="field-input" name="category" required>${categoryOptionsHtml(category)}</select>
          </label>
        </div>
        <p class="expense-quick__status" id="expense-quick-status" hidden></p>
        <button type="submit" class="btn-primary expense-quick-modal__confirm">${confirmLabel}</button>
      </form>
    </div>
  `;

  document.body.appendChild(modalEl);
  dismissHistory = trapModalHistory(destroyModalFromBack);

  modalEl.querySelectorAll('[data-close-expense]').forEach((el) => {
    el.addEventListener('click', closeModal);
  });

  const form = modalEl.querySelector('#expense-quick-form');
  const status = modalEl.querySelector('#expense-quick-status');
  const amountInput = form.querySelector('[name="amount"]');
  if (!amountValue) amountInput.focus();

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const fd = new FormData(form);
    const submitBtn = form.querySelector('[type="submit"]');
    submitBtn.disabled = true;
    status.hidden = false;
    status.textContent = 'Salvataggio…';
    status.className = 'expense-quick__status';

    try {
      if (isEdit) {
        await updateExpense(expenseId, {
          dayKey: fd.get('dayKey'),
          label: String(fd.get('label')).trim(),
          amountEur: Number(fd.get('amount')),
          category: String(fd.get('category') ?? 'altro'),
        });
        status.textContent = 'Spesa aggiornata';
      } else {
        await addExpense(
          instanceId,
          fd.get('dayKey'),
          String(fd.get('label')).trim(),
          Number(fd.get('amount')),
          String(fd.get('category') ?? 'altro'),
        );
        status.textContent = 'Spesa registrata';
      }
      status.classList.add('expense-quick__status--ok');
      onSuccess?.();
      setTimeout(closeModal, 600);
    } catch (err) {
      status.textContent = err.message;
      status.classList.add('expense-quick__status--err');
      submitBtn.disabled = false;
    }
  });
}

export const EXPENSE_DRAFT_KEY = 'metis-expense-draft';

export function setExpenseDraft(draft) {
  try {
    sessionStorage.setItem(EXPENSE_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    /* ignore */
  }
}

export function consumeExpenseDraft() {
  try {
    const raw = sessionStorage.getItem(EXPENSE_DRAFT_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(EXPENSE_DRAFT_KEY);
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
